const GUARDIAN_API_BASE =
  process.env.REACT_APP_GUARDIAN_API_URL || 'http://localhost:3000/api/v1/guardian';

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function normalizeLocations(raw) {
  const data = raw?.data ?? raw;
  const locations = data?.locations;
  if (!Array.isArray(locations)) return [];

  return locations
    .filter(
      (loc) =>
        loc.latitude != null &&
        loc.longitude != null &&
        loc.display_name
    )
    .map((loc) => ({
      lat: loc.latitude,
      lng: loc.longitude,
      placeName: loc.display_name,
    }));
}

async function fetchBackendSearch(token, query, limit) {
  try {
    const params = new URLSearchParams({ q: query.trim(), limit: String(limit) });
    const response = await fetch(
      `${GUARDIAN_API_BASE}/geocode/search?${params}`,
      { headers: authHeaders(token) }
    );
    if (!response.ok) return [];
    const raw = await response.json();
    return normalizeLocations(raw);
  } catch {
    return [];
  }
}

function dedupeResults(results) {
  const seen = new Set();
  return results.filter((result) => {
    const key = `${result.placeName.toLowerCase()}|${result.lat.toFixed(5)}|${result.lng.toFixed(5)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function searchGeocode(token, query, limit = 5) {
  if (!query?.trim() || query.trim().length < 2) return [];

  const trimmed = query.trim();
  const backendResults = await fetchBackendSearch(token, trimmed, limit);

  return dedupeResults(backendResults).slice(0, limit);
}

export async function forwardGeocode(token, query) {
  const results = await searchGeocode(token, query, 1);
  return results[0] || null;
}

const REVERSE_GEOCODE_TIMEOUT_MS = 5000;

/** AbortSignal.timeout, with a fallback for browsers without it (Safari < 16). */
function timeoutSignal(ms) {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  if (typeof AbortController === 'undefined') return undefined;
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

export async function reverseGeocode(token, lat, lng) {
  try {
    const params = new URLSearchParams({
      lat: String(lat),
      lng: String(lng),
    });
    const response = await fetch(
      `${GUARDIAN_API_BASE}/geocode/reverse?${params}`,
      {
        headers: authHeaders(token),
        // A stalled lookup must not hold a near-me turn; the caller falls
        // back to coordinates only (2026-09-25 pass 4 C1).
        signal: timeoutSignal(REVERSE_GEOCODE_TIMEOUT_MS),
      }
    );
    if (!response.ok) return null;
    const raw = await response.json();
    return raw?.data?.display_name || null;
  } catch {
    return null;
  }
}
